import { BaseClient } from '../BaseClient';
import { Empty } from '../compiled-protos/common';
import { Event } from '../compiled-protos/event';
import type {
  BudgetUpdateRequest,
  ConvertToProjectRequest,
  CreateProjectRequest,
  DepartmentMappingRequest,
  FormStatus,
  FormType,
  ImportContractRequest,
  InvoiceStatusRequest,
  MarkFollowedUpRequest,
  MobileProjectRequest,
  ProjectCSIDivision,
  ProjectCSISubtype,
  ProjectDashboardRequest,
  ProjectProgressUpdate,
  SnoozeFollowUpRequest,
  StatusChangeRequest,
  SyncBudgetRequest,
  SyncStatusRequest,
  TimeEntry,
  TimeEntryRequest,
  UpdateProgressRequest,
} from '../compiled-protos/project_management';
import {
  BackfillQBOInvoicesResponse,
  BudgetCalculationRequest,
  DepartmentMappingResponse,
  FormSubmission,
  FormSubmissionDocument,
  ItemizedActualsBreakdown,
  MobileProjectList,
  NTORequestDetail,
  ProjectActivity,
  ProjectBudgetSummary,
  ProjectDashboard,
  ProjectDepartmentAssignment,
  ProjectEstimate,
  ProjectInvoice,
  ProjectScopeDetail,
  SubcontractRequestDetail,
  SyncStatusResponse,
  TimeEntryData,
  TimeEntryValidation,
  UnityBuildContract,
} from '../compiled-protos/project_management';
import { ProjectManagementServiceClient } from '../compiled-protos/project_management.client';

class ProjectManagementClient extends BaseClient {
  self: ProjectManagementServiceClient;

  constructor(host: string, userID?: number) {
    super(host, userID);
    this.self = new ProjectManagementServiceClient(this.transport);
  }

  // Project Activity Methods
  public async CreateProjectActivity(req: ProjectActivity) {
    let res = ProjectActivity.create();
    try {
      res = await this.self.createProjectActivity(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async GetProjectActivity(req: ProjectActivity) {
    const res = await this.self.batchGetProjectActivity(req, this.getMetaDataWithoutCache()).response;
    return res;
  }

  public async BatchGetProjectActivity(req: ProjectActivity) {
    const res = await this.self.batchGetProjectActivity(req, this.getMetaDataWithoutCache()).response;
    return res;
  }



  // Project Budget Summary Methods
  public async CalculateProjectBudget(req: BudgetCalculationRequest) {
    let res = ProjectBudgetSummary.create();
    try {
      res = await this.self.calculateProjectBudget(req, this.getMetaDataWithoutCache()).response;
    } catch (err) {
      console.error('Error calculating project budget:', err);
      throw new Error(`Failed to calculate project budget: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
    return res;
  }

  // Project Department Assignment Methods  
  public async CreateProjectDepartmentAssignment(req: ProjectDepartmentAssignment) {
    let res = ProjectDepartmentAssignment.create();
    try {
      res = await this.self.createProjectDepartmentAssignment(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async GetProjectDepartmentAssignment(req: ProjectDepartmentAssignment) {
    let res = ProjectDepartmentAssignment.create();
    try {
      res = await this.self.getProjectDepartmentAssignment(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async BatchGetProjectDepartmentAssignment(req: ProjectDepartmentAssignment) {
    const res = await this.self.batchGetProjectDepartmentAssignment(req, this.getMetaDataWithoutCache()).response;
    return res;
  }

  public async UpdateProjectDepartmentAssignment(req: ProjectDepartmentAssignment) {
    let res = ProjectDepartmentAssignment.create();
    try {
      res = await this.self.updateProjectDepartmentAssignment(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async DeleteProjectDepartmentAssignment(req: ProjectDepartmentAssignment) {
    let res = ProjectDepartmentAssignment.create();
    try {
      res = await this.self.deleteProjectDepartmentAssignment(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  // Project Invoice Methods
  public async CreateProjectInvoice(req: ProjectInvoice) {
    let res = ProjectInvoice.create();
    try {
      res = await this.self.createProjectInvoice(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async GetProjectInvoice(req: ProjectInvoice) {
    let res = ProjectInvoice.create();
    try {
      res = await this.self.getProjectInvoice(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async BatchGetProjectInvoice(req: ProjectInvoice) {
    const res = await this.self.batchGetProjectInvoice(req, this.getMetaDataWithoutCache()).response;
    return res;
  }

  public async UpdateProjectInvoice(req: ProjectInvoice) {
    let res = ProjectInvoice.create();
    try {
      res = await this.self.updateProjectInvoice(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async DeleteProjectInvoice(req: ProjectInvoice) {
    let res = ProjectInvoice.create();
    try {
      res = await this.self.deleteProjectInvoice(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async BackfillQBOInvoices() {
    let res = BackfillQBOInvoicesResponse.create();
    try {
      res = await this.self.backfillQBOInvoices(Empty.create(), this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  // Unity Build Integration Methods
  public async ImportUnityBuildContract(req: ImportContractRequest) {
    let res = UnityBuildContract.create();
    try {
      res = await this.self.importUnityBuildContract(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async SyncBudgetWithUnity(req: SyncBudgetRequest) {
    let res = ProjectBudgetSummary.create();
    try {
      res = await this.self.syncContractBudget(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async GetDepartmentMapping(req: DepartmentMappingRequest) {
    let res = DepartmentMappingResponse.create();
    try {
      res = await this.self.mapDepartments(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async GetSyncStatus(req: SyncStatusRequest) {
    let res = SyncStatusResponse.create();
    try {
      res = await this.self.getSyncStatus(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  // Mobile Project Methods
  public async GetMobileProjects(req: MobileProjectRequest) {
    let res = MobileProjectList.create();
    try {
      res = await this.self.getUserProjects(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  // Time Entry Methods

  public async GetTimeEntry(req: TimeEntryRequest) {
    let res = TimeEntryData.create();
    try {
      res = await this.self.getProjectTimeEntryData(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }



  public async ValidateTimeEntry(req: TimeEntry) {
    let res = TimeEntryValidation.create();
    try {
      res = await this.self.validateTimeEntry(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  // Project Management Methods
  public async CreateProject(req: CreateProjectRequest) {
    let res = Event.create();
    try {
      res = await this.self.createProjectFromEvent(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async CreateProjectWithDepartmentScopes(req: any) {
    try {
      const res = await this.self.createProjectWithDepartmentScopes(req, this.getMetaData()).response;
      return res;
    } catch (err) {
      console.error('Error creating project with department scopes:', err);
      throw err;
    }
  }

  public async ConvertToProject(req: ConvertToProjectRequest) {
    let res = Event.create();
    try {
      res = await this.self.convertServiceToProject(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async GetProjectDashboard(req: ProjectDashboardRequest) {
    let res = ProjectDashboard.create();
    try {
      res = await this.self.getProjectDashboard(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  // Status and Budget Management
  public async LogStatusChange(req: StatusChangeRequest) {
    let res = ProjectActivity.create();
    try {
      res = await this.self.logStatusChange(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async LogBudgetUpdate(req: BudgetUpdateRequest) {
    let res = ProjectActivity.create();
    try {
      res = await this.self.logBudgetUpdate(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async UpdateInvoiceStatus(req: InvoiceStatusRequest) {
    let res = ProjectInvoice.create();
    try {
      res = await this.self.updateInvoiceStatus(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  // Convenience Methods
  public async loadProjectActivity(projectEventId: number) {
    const req = ProjectActivity.create();
    req.projectEventId = projectEventId;
    req.orderBy = 'created_at';
    req.orderDir = 'desc';
    return await this.BatchGetProjectActivity(req);
  }

  public async loadDepartmentAssignments(projectEventId: number) {
    const req = ProjectDepartmentAssignment.create();
    req.projectEventId = projectEventId;
    req.isActive = true;
    return await this.BatchGetProjectDepartmentAssignment(req);
  }

  public async calculateProjectBudget(projectEventId: number, includeActuals = true, includeItemizedBreakdown = true) {
    const req = BudgetCalculationRequest.create();
    req.projectEventId = projectEventId;
    req.includeActuals = includeActuals;
    req.includeVariance = true;
    req.includeItemizedBreakdown = includeItemizedBreakdown;
    req.departmentIds = []; // Empty array means all departments
    return await this.CalculateProjectBudget(req);
  }

  public async GetItemizedActuals(req: BudgetCalculationRequest) {
    let res = ItemizedActualsBreakdown.create();
    try {
      res = await this.self.getItemizedActuals(req, this.getMetaDataWithoutCache()).response;
    } catch (err) {
      console.error('Error getting itemized actuals:', err);
      throw new Error(`Failed to get itemized actuals: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
    return res;
  }

  public async getItemizedActualsForEstimate(eventId: number) {
    const req = BudgetCalculationRequest.create();
    req.projectEventId = eventId;
    req.includeActuals = true;
    req.includeVariance = false;
    req.includeItemizedBreakdown = true;
    req.departmentIds = []; // Empty array means all departments
    return await this.GetItemizedActuals(req);
  }

  // Form Type Methods
  public async BatchGetFormType(req: FormType) {
    const res = await this.self.batchGetFormType(req, this.getMetaDataWithoutCache()).response;
    return res;
  }

  // Form Status Methods
  public async BatchGetFormStatus(req: FormStatus) {
    const res = await this.self.batchGetFormStatus(req, this.getMetaDataWithoutCache()).response;
    return res;
  }

  // Project CSI Division Methods
  public async BatchGetProjectCSIDivision(req: ProjectCSIDivision) {
    const res = await this.self.batchGetProjectCSIDivision(req, this.getMetaDataWithoutCache()).response;
    return res;
  }

  // Project CSI Subtype Methods
  public async BatchGetProjectCSISubtype(req: ProjectCSISubtype) {
    const res = await this.self.batchGetProjectCSISubtype(req, this.getMetaDataWithoutCache()).response;
    return res;
  }

  // Form Submission Methods
  public async CreateFormSubmission(req: FormSubmission) {
    let res = FormSubmission.create();
    try {
      res = await this.self.createFormSubmission(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async GetFormSubmission(req: FormSubmission) {
    let res = FormSubmission.create();
    try {
      res = await this.self.getFormSubmission(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async BatchGetFormSubmission(req: FormSubmission) {
    const res = await this.self.batchGetFormSubmission(req, this.getMetaDataWithoutCache()).response;
    return res;
  }

  public async UpdateFormSubmission(req: FormSubmission) {
    let res = FormSubmission.create();
    try {
      res = await this.self.updateFormSubmission(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async DeleteFormSubmission(req: FormSubmission) {
    let res = FormSubmission.create();
    try {
      res = await this.self.deleteFormSubmission(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async SubmitForm(req: FormSubmission) {
    let res = FormSubmission.create();
    try {
      res = await this.self.submitForm(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  // Form Submission Document Methods
  public async CreateFormSubmissionDocument(req: FormSubmissionDocument) {
    let res = FormSubmissionDocument.create();
    try {
      res = await this.self.createFormSubmissionDocument(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async BatchGetFormSubmissionDocument(req: FormSubmissionDocument) {
    const res = await this.self.batchGetFormSubmissionDocument(req, this.getMetaDataWithoutCache()).response;
    return res;
  }

  public async DeleteFormSubmissionDocument(req: FormSubmissionDocument) {
    let res = FormSubmissionDocument.create();
    try {
      res = await this.self.deleteFormSubmissionDocument(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  // Subcontract Request Detail Methods
  public async CreateSubcontractRequestDetail(req: SubcontractRequestDetail) {
    let res = SubcontractRequestDetail.create();
    try {
      res = await this.self.createSubcontractRequestDetail(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async GetSubcontractRequestDetail(req: SubcontractRequestDetail) {
    let res = SubcontractRequestDetail.create();
    try {
      res = await this.self.getSubcontractRequestDetail(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async UpdateSubcontractRequestDetail(req: SubcontractRequestDetail) {
    let res = SubcontractRequestDetail.create();
    try {
      res = await this.self.updateSubcontractRequestDetail(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  // NTO Request Detail Methods
  public async CreateNTORequestDetail(req: NTORequestDetail) {
    let res = NTORequestDetail.create();
    try {
      res = await this.self.createNTORequestDetail(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async GetNTORequestDetail(req: NTORequestDetail) {
    let res = NTORequestDetail.create();
    try {
      res = await this.self.getNTORequestDetail(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async UpdateNTORequestDetail(req: NTORequestDetail) {
    let res = NTORequestDetail.create();
    try {
      res = await this.self.updateNTORequestDetail(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  // Project Scope Detail Methods
  public async CreateProjectScopeDetail(req: ProjectScopeDetail) {
    let res = ProjectScopeDetail.create();
    try {
      res = await this.self.createProjectScopeDetail(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async GetProjectScopeDetail(req: ProjectScopeDetail) {
    let res = ProjectScopeDetail.create();
    try {
      res = await this.self.getProjectScopeDetail(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async UpdateProjectScopeDetail(req: ProjectScopeDetail) {
    let res = ProjectScopeDetail.create();
    try {
      res = await this.self.updateProjectScopeDetail(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  // Project Estimate Methods (kalos2 schema)
  public async CreateProjectEstimate(req: ProjectEstimate) {
    let res = ProjectEstimate.create();
    try {
      res = await this.self.createProjectEstimate(req, this.getMetaData()).response;
    } catch (err) {
      console.error('Error creating project estimate:', err);
      throw err;
    }
    return res;
  }

  public async GetProjectEstimate(req: ProjectEstimate) {
    let res = ProjectEstimate.create();
    try {
      res = await this.self.getProjectEstimate(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async BatchGetProjectEstimate(req: ProjectEstimate) {
    const res = await this.self.batchGetProjectEstimate(req, this.getMetaDataWithoutCache()).response;
    return res;
  }

  public async UpdateProjectEstimate(req: ProjectEstimate) {
    let res = ProjectEstimate.create();
    try {
      res = await this.self.updateProjectEstimate(req, this.getMetaData()).response;
    } catch (err) {
      console.error('Error updating project estimate:', err);
      throw err;
    }
    return res;
  }

  public async DeleteProjectEstimate(req: ProjectEstimate) {
    let res = ProjectEstimate.create();
    try {
      res = await this.self.deleteProjectEstimate(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  // Progress Tracking Methods
  public async UpdateProjectProgress(req: UpdateProgressRequest) {
    const res = await this.self.updateProjectProgress(req, this.getMetaData()).response;
    return res;
  }

  public async GetProjectProgressHistory(req: ProjectProgressUpdate) {
    const res = await this.self.getProjectProgressHistory(req, this.getMetaDataWithoutCache()).response;
    return res;
  }

  public async GetProjectHealthMetrics(req: BudgetCalculationRequest) {
    const res = await this.self.getProjectHealthMetrics(req, this.getMetaDataWithoutCache()).response;
    return res;
  }

  public async getHealthMetrics(projectEventId: number) {
    const req = BudgetCalculationRequest.create();
    req.projectEventId = projectEventId;
    return await this.GetProjectHealthMetrics(req);
  }

  // Follow-up loop (CU-86batet2w slice 3)
  public async MarkProjectFollowedUp(req: MarkFollowedUpRequest) {
    const res = await this.self.markProjectFollowedUp(req, this.getMetaData()).response;
    return res;
  }

  public async SnoozeProjectFollowUp(req: SnoozeFollowUpRequest) {
    const res = await this.self.snoozeProjectFollowUp(req, this.getMetaData()).response;
    return res;
  }
}

export {
  BackfillQBOInvoicesResponse,
  BudgetBreakdown,
  BudgetCalculationRequest,
  BudgetUpdateRequest,
  BulkDepartmentAssignRequest,
  ConvertToProjectRequest,
  CreateProjectRequest,
  CreateProjectWithScopesRequest,
  CreateProjectWithScopesResponse,
  DepartmentBudgetAllocation,
  DepartmentMapping,
  DepartmentMappingRequest,
  DepartmentMappingResponse,
  DepartmentScopeEvent,
  FormSubmission,
  FormSubmissionList,
  FormSubmissionDocument,
  FormSubmissionDocumentList,
  FormType,
  FormTypeList,
  FormStatus,
  FormStatusList,
  SubcontractRequestDetail,
  NTORequestDetail,
  ProjectScopeDetail,
  ProjectCSIDivision,
  ProjectCSIDivisionList,
  ProjectCSISubtype,
  ProjectCSISubtypeList,
  ImportContractRequest,
  InvoiceStatusRequest,
  GeneralExpenseExtras,
  ItemizedActualsBreakdown,
  ItemizedCategoryBreakdown,
  MarkFollowedUpRequest,
  SnoozeFollowUpRequest,
  MobileProjectList,
  MobileProjectRequest,
  MobileProjectSummary,
  ProjectActivity,
  ProjectActivityList,
  ProjectBudgetSummary,
  ProjectDashboard,
  ProjectDashboardRequest,
  ProjectDepartmentAssignment,
  ProjectDepartmentAssignmentList,
  ProjectEstimate,
  ProjectEstimateList,
  ProjectHealthMetrics,
  ProjectInvoice,
  ProjectInvoiceList,
  ProjectProgressUpdate,
  ProjectProgressUpdateList,
  StatusChangeRequest,
  SyncBudgetRequest,
  SyncStatusRequest,
  SyncStatusResponse,
  TimeEntry,
  TimeEntryData,
  TimeEntryRequest,
  TimeEntryValidation,
  UnityBuildContract,
  UnityBuildLineItem,
  UpdateProgressRequest,
} from '../compiled-protos/project_management';

export { ProjectManagementClient };